import { FlexCol, FlexRow } from '@/components/common/Flex';
import { LinkText, Paragraph, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { ListItem, UnorderedList } from '@chakra-ui/react';
import { EmergencyFundGoal } from '@common/goals';
import { getGoalBaseConfig } from '@common/react/goals';
import { JobSearchDuration } from '@common/types';
import { dt, isPastDate } from '@common/util/dateTime';
import { capitalize } from '@common/util/strings';
import { isEmpty, isNumber, isString } from '@common/util/types';
import isEqual from 'lodash.isequal';
import GoalAssumptionsInfoLink from '../GoalAssumptionsInfoLink';
import GoalStatementLinkText from '../GoalStatementLinkText';
import {
    getFinalAssumptionsWithoutRisk,
    getIOrWe,
    getMonthsStr,
    getTargetDateFromYearMonth,
    getYearsOldStr,
} from '../helpers';
import { GoalConfig, GoalData } from './types';

const jobSearchDurationLabelByValue: Record<JobSearchDuration, string> = {
    SUB_2_MONTHS: '< 2 months',
    '2_PLUS_MONTHS': '2+ months',
} as const;

export const EMERGENCY_FUND: GoalConfig<EmergencyFundGoal> = {
    inputSteps: [
        {
            id: 'jobSearchDuration',
            title: 'If you needed to look for a new job, how long may it take?',
            fieldConfigs: [
                {
                    fieldName: 'jobSearchDuration',
                    fieldType: 'radioCards',
                    fieldOptions: Object.entries(
                        jobSearchDurationLabelByValue
                    ).map(([value, label]) => ({ value, label })),
                    bottomContent: ({ showSlideup }) => {
                        return (
                            <FlexRow mt={8}>
                                <GoalAssumptionsInfoLink
                                    onClick={() =>
                                        showSlideup('jobSearchDurationInfo')
                                    }
                                    onClickEvent="Goal Assumptions Info Button Clicked"
                                    onClickEventParams={{
                                        location:
                                            'Emergency Fund Goal Guidance',
                                        card_id: 'jobSearchDuration',
                                        label: 'Why we ask this',
                                    }}
                                >
                                    {'Why we ask this'}
                                </GoalAssumptionsInfoLink>
                            </FlexRow>
                        );
                    },
                },
            ],
        },
        {
            id: 'isHomeOwner',
            title: 'Do you own a home?',
            fieldConfigs: [
                {
                    fieldName: 'isHomeOwner',
                    fieldType: 'radioCards',
                    fieldOptions: [
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ],
                    bottomContent: ({ showSlideup }) => {
                        return (
                            <FlexRow mt={8}>
                                <GoalAssumptionsInfoLink
                                    onClick={() =>
                                        showSlideup('isHomeOwnerInfo')
                                    }
                                    onClickEvent="Goal Assumptions Info Button Clicked"
                                    onClickEventParams={{
                                        location:
                                            'Emergency Fund Goal Guidance',
                                        card_id: 'isHomeOwner',
                                        label: 'Why we ask this',
                                    }}
                                >
                                    {'Why we ask this'}
                                </GoalAssumptionsInfoLink>
                            </FlexRow>
                        );
                    },
                },
            ],
        },
        {
            id: 'monthlyExpenses',
            title: 'What’s a rough estimate of your monthly spend on necessities?',
            fieldConfigs: [
                {
                    fieldName: 'monthlyExpenses',
                    fieldType: 'currency',
                    fieldValueDefault: 4_000_00, // $4,000
                    min: 0,
                    slider: {
                        min: 0,
                        max: 20_000_00, // $20,000
                        step: 1_000_00, // $1,000
                    },
                    bottomContent: (
                        <Paragraph
                            fontSize="sm"
                            color="textMuted"
                            mt={4}
                        >{`Necessities include things like your rent or mortgage, food, medical expenses, insurance, transportation, utilities, etc..`}</Paragraph>
                    ),
                },
            ],
        },
        {
            id: 'targetAmount',
            title: `Choose a target emergency fund that you’re comfortable with.`,
            fieldConfigs: [
                {
                    fieldName: 'targetAmount',
                    fieldType: 'currency',
                    min: 0,
                    slider: ({ goalData }) => {
                        return isNumber(goalData.projectedAmount) &&
                            goalData.projectedAmount > 0
                            ? {
                                  min: 0,
                                  max: goalData.projectedAmount * 2,
                                  step: 1_000_00, // $1,000
                              }
                            : {
                                  min: 0,
                                  max: 100_000_00, // $100,000
                                  step: 1_000_00, // $1,000
                              };
                    },
                    bottomContent: ({ goalData, showSlideup }) => {
                        return (
                            <FlexCol mt={4}>
                                {isNumber(goalData.targetMonths) &&
                                goalData.targetMonths > 0 ? (
                                    <Text fontSize="lg" color="textMuted">
                                        {getMonthsStr(goalData.targetMonths)}
                                    </Text>
                                ) : null}
                                {isNumber(goalData.projectedAmount) &&
                                    isNumber(goalData.projectedMonths) &&
                                    goalData.projectedMonths > 0 && (
                                        <>
                                            <Paragraph
                                                mt={4}
                                                fontSize="sm"
                                                color="textMuted"
                                            >
                                                {`We recommend an emergency fund of ${toDollars(
                                                    goalData.projectedAmount
                                                )}, which will give you ${getMonthsStr(
                                                    goalData.projectedMonths
                                                )} of security.`}
                                            </Paragraph>
                                            <LinkText
                                                fontSize="sm"
                                                fontWeight="medium"
                                                mt={3}
                                                onClick={() => {
                                                    showSlideup(
                                                        'targetAmountAssumptions'
                                                    );
                                                }}
                                            >
                                                See assumptions
                                            </LinkText>
                                        </>
                                    )}
                            </FlexCol>
                        );
                    },
                },
            ],
        },
        {
            id: 'yearMonth',
            title: 'Roughly, when do you want to reach your target amount by?',
            fieldConfigs: [
                {
                    fieldName: 'yearMonth',
                    fieldType: 'yearMonth',
                    bottomContent: ({ goalData, userInfo }) => (
                        <Paragraph fontSize="sm" color="textMuted" mt={4} h={5}>
                            {goalData.targetDate
                                ? getYearsOldStr({
                                      date: goalData.targetDate,
                                      userInfo,
                                  })
                                : undefined}
                        </Paragraph>
                    ),
                },
            ],
            isNextButtonDisabled: (goalData) =>
                !goalData.targetDate || isPastDate(goalData.targetDate),
        },
    ],
    assumptions: [
        {
            id: 'jobSearchDurationInfo',
            title: 'Why we ask this',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        Life can be full of surprises. No matter the reason, we
                        want to make sure you have some spare cash in case you
                        ever need to look for a new job. For some, finding a new
                        job can be a few applications and a matter of weeks. For
                        others, you may need some time to find the right person,
                        find a good fit with the company/team or get additional
                        training, all of which takes time.
                    </Paragraph>
                </>
            ),
        },
        {
            id: 'isHomeOwnerInfo',
            title: 'Why we ask this',
            content: () => (
                <>
                    <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                        A good way to be prepared for the unexpected, is to
                        expect it. When you own a home, you’ll be responsible
                        for things small and large that your landlord used to
                        do. Water heater stops working? That’s at least $1k.
                        Furnace breaks? Replacements can be $4.5k. Burst pipe?
                        $4k. Mice or rats? $1k. These things will likely happen
                        at one point or other, so having some money saved for it
                        means you’ll stay far away from expensive debt.
                    </Paragraph>
                </>
            ),
        },
        {
            id: 'targetAmountAssumptions',
            content: ({ goalData }) => {
                return (
                    <>
                        <Paragraph fontSize="sm" lineHeight="tall" mb={1}>
                            To calculate a target emergency fund, we took into
                            account:
                        </Paragraph>
                        <UnorderedList
                            fontSize="sm"
                            lineHeight="tall"
                            spacing={1}
                        >
                            {goalData.monthlyExpenses && (
                                <ListItem>
                                    Your monthly necessities are{' '}
                                    {toDollars(goalData.monthlyExpenses)} per
                                    month
                                </ListItem>
                            )}
                            {goalData.jobSearchDuration && (
                                <ListItem>
                                    It would take{' '}
                                    {goalData.jobSearchDuration ===
                                    'SUB_2_MONTHS'
                                        ? '< 2 months'
                                        : '2+ months'}{' '}
                                    to find a new job
                                </ListItem>
                            )}
                            {goalData.isHomeOwner !== undefined && (
                                <ListItem>
                                    {goalData.isHomeOwner
                                        ? 'You own a home'
                                        : 'You don’t own a home'}
                                </ListItem>
                            )}
                            {/* TODO: Provide for kids */}
                            {/* TODO: Partner income */}
                        </UnorderedList>
                    </>
                );
            },
        },
    ],
    getGoalStatementContent: ({ goalData, onGoalStatementClick }) => {
        if (
            !isNumber(goalData.targetAmount) ||
            !isString(goalData.targetDate)
        ) {
            console.warn(
                'Failed to construct goal statement due to incomplete goalData.'
            );
            console.warn(goalData);
            return null;
        }

        const iOrWe = getIOrWe(goalData);
        const color = EMERGENCY_FUND.color;

        return (
            <>
                {`${capitalize(iOrWe)} want to have `}
                <GoalStatementLinkText
                    text={toDollars(goalData.targetAmount)}
                    onClick={() => onGoalStatementClick('targetAmount')}
                    color={color}
                />
                {isNumber(goalData.targetMonths) &&
                    goalData.targetMonths > 0 &&
                    ` for ${getMonthsStr(goalData.targetMonths)} of expenses `}
                {` by `}
                <GoalStatementLinkText
                    text={dt(goalData.targetDate).format('MMMM, YYYY')}
                    onClick={() => onGoalStatementClick('yearMonth')}
                    color={color}
                />
                {`.`}
            </>
        );
    },
    getAssumptionsContent: getFinalAssumptionsWithoutRisk,
    calculateDerivedGoalData: async ({
        oldGoalData,
        newGoalData,
    }): Promise<GoalData<EmergencyFundGoal>> => {
        // TODO: Incorporate partner's income and having kids into this logic
        const projectedMonths =
            !isEmpty(newGoalData.jobSearchDuration) &&
            !isEmpty(newGoalData.isHomeOwner)
                ? 3 +
                  (newGoalData.jobSearchDuration === '2_PLUS_MONTHS' ? 1 : 0) +
                  (newGoalData.isHomeOwner ? 1 : 0)
                : undefined;

        const targetMonths =
            isNumber(newGoalData.targetAmount) &&
            isNumber(newGoalData.monthlyExpenses) &&
            newGoalData.monthlyExpenses > 0
                ? // rounded down to represent complete months
                  Math.floor(
                      newGoalData.targetAmount / newGoalData.monthlyExpenses
                  )
                : undefined;

        const targetDate =
            oldGoalData.yearMonth !== newGoalData.yearMonth &&
            newGoalData.yearMonth?.length === 6
                ? getTargetDateFromYearMonth(newGoalData.yearMonth)
                : oldGoalData.targetDate;

        const projectedAmount =
            isNumber(newGoalData.monthlyExpenses) && isNumber(projectedMonths)
                ? newGoalData.monthlyExpenses * projectedMonths
                : undefined;

        return {
            ...newGoalData,
            projectedMonths,
            projectedAmount,
            targetMonths,
            targetDate,

            // update targetAmount if projected amount changed using formula above
            ...(!isEqual(oldGoalData.projectedAmount, projectedAmount) && {
                targetAmount: projectedAmount,
                targetMonths: projectedMonths,
            }),
        };
    },
    getRecommendedRiskScore: () => 0,
    getTargetDate: ({ goalData }) => goalData.targetDate,
    ...getGoalBaseConfig('EMERGENCY_FUND'),
};
